(() => {
  if (window.__tmGiftAtomicP0Installed) return;
  window.__tmGiftAtomicP0Installed = true;

  const config = window.TM_GIFT_AUTO_ADD || {};
  const giftVariantId = Number(config.giftVariantId || 0);
  const qualifyAnyNonGiftItem = config.qualifyAnyNonGiftItem === true;
  const giftProductId = Number(config.giftProductId || 0);
  const qualifyingProductIds = (config.qualifyingProductIds || []).map(Number);
  const qualifyingVariantIds = (config.qualifyingVariantIds || []).map(Number);
  const cartUrl = config.cartUrl || '/cart';
  let cartPromise = null;
  let cachedCart = null;
  let ensurePromise = null;
  let ensureTimer = null;

  if (!giftVariantId || !giftProductId) return;

  function request(url, options = {}) {
    return fetch(url, {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    }).then(async (response) => {
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.description || body.message || `Cart request failed: ${response.status}`);
      return body;
    });
  }

  function getCart(fresh = false) {
    if (!fresh && cachedCart) return Promise.resolve(cachedCart);
    if (!fresh && cartPromise) return cartPromise;
    const promise = request(`${cartUrl}.js?tm_p0=${Date.now()}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }).then((cart) => {
      cachedCart = cart;
      return cart;
    });
    cartPromise = promise.finally(() => {
      cartPromise = null;
    });
    return cartPromise;
  }

  function prime() {
    return getCart(false).catch(() => null);
  }

  function withTimeout(promise, timeoutMs) {
    return Promise.race([
      promise,
      new Promise((resolve) => window.setTimeout(() => resolve(null), timeoutMs)),
    ]);
  }

  function isGift(line) {
    return Number(line?.variant_id) === giftVariantId || Number(line?.product_id) === giftProductId;
  }

  function isQualifying(line) {
    if (!line || isGift(line)) return false;
    if (qualifyAnyNonGiftItem) return true;
    return (
      qualifyingProductIds.includes(Number(line.product_id)) ||
      qualifyingVariantIds.includes(Number(line.variant_id))
    );
  }

  function formDataItem(formData) {
    const item = {
      id: Number(formData.get('id')),
      quantity: Math.max(1, Number(formData.get('quantity') || 1)),
    };
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = /^properties\[(.+)\]$/.exec(key);
      if (match && value !== '') properties[match[1]] = value;
    }
    if (Object.keys(properties).length) item.properties = properties;
    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) item.selling_plan = Number(sellingPlan);
    return item;
  }

  async function buildAddRequest(formData, sections, sectionsUrl, knownCart = null) {
    const mainItem = formDataItem(formData);
    if (Number(mainItem.id) === giftVariantId) return null;
    if (!qualifyAnyNonGiftItem && !qualifyingVariantIds.includes(Number(mainItem.id))) return null;

    const cart = knownCart?.items ? knownCart : await withTimeout(prime(), 900);
    if (!cart || (cart.items || []).some(isGift)) return null;

    const payload = {
      items: [
        mainItem,
        {
          id: giftVariantId,
          quantity: 1,
          properties: { _tm_gift: 'Complimentary Charm Necklace' },
        },
      ],
      sections,
      sections_url: sectionsUrl,
    };
    return {
      url: `${window.routes?.cart_add_url || '/cart/add'}.js`,
      options: {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      },
      giftIncluded: true,
    };
  }

  function addGift() {
    return request('/cart/add.js?tm_gift_p0=1', {
      method: 'POST',
      body: JSON.stringify({
        items: [
          {
            id: giftVariantId,
            quantity: 1,
            properties: { _tm_gift: 'Complimentary Charm Necklace' },
          },
        ],
      }),
    });
  }

  function changeLine(key, quantity) {
    return request('/cart/change.js?tm_gift_p0=1', {
      method: 'POST',
      body: JSON.stringify({ id: key, quantity }),
    });
  }

  function publishCartUpdate(cart) {
    try {
      window.publish?.(window.PUB_SUB_EVENTS?.cartUpdate, {
        source: 'tm-gift-atomic-p0',
        cartData: cart,
      });
    } catch (_) {
      // Rendering the cart must not fail because an optional subscriber failed.
    }
  }

  function fetchPopupSections() {
    const params = new URLSearchParams({
      sections: 'cart-notification-product,cart-notification-button,cart-icon-bubble',
      tm_gift_p0: String(Date.now()),
    });
    return fetch(`${cartUrl}?${params}`, {
      credentials: 'same-origin',
      cache: 'no-store',
    }).then((response) => {
      if (!response.ok) throw new Error(`Section refresh failed: ${response.status}`);
      return response.json();
    });
  }

  async function refreshPopup(cart) {
    const notification = document.querySelector('cart-notification');
    if (!notification || typeof notification.renderSections !== 'function') return;
    const sections = await fetchPopupSections();
    notification.renderSections(sections);
    publishCartUpdate(cart);
  }

  async function ensure(options = {}) {
    if (ensurePromise) return ensurePromise;
    ensurePromise = (async () => {
      let cart = await getCart(true);
      const items = cart.items || [];
      const qualifies = items.some(isQualifying);
      const giftLines = items.filter(isGift);
      let changed = false;

      if (qualifies && giftLines.length === 0) {
        await addGift();
        changed = true;
      } else if (qualifies && giftLines.length > 0) {
        for (let index = 0; index < giftLines.length; index += 1) {
          const desired = index === 0 ? 1 : 0;
          if (Number(giftLines[index].quantity) !== desired) {
            await changeLine(giftLines[index].key, desired);
            changed = true;
          }
        }
      } else if (!qualifies && giftLines.length) {
        for (const line of giftLines) await changeLine(line.key, 0);
        changed = true;
      }

      if (changed) cart = await getCart(true);
      cachedCart = cart;
      if (changed && options.refreshPopup) await refreshPopup(cart);
      return cart;
    })()
      .catch((error) => {
        window.__tmGiftAtomicP0LastError = String(error?.message || error);
        return null;
      })
      .finally(() => {
        ensurePromise = null;
      });
    return ensurePromise;
  }

  function scheduleEnsure(delay = 180, options = {}) {
    window.clearTimeout(ensureTimer);
    ensureTimer = window.setTimeout(() => ensure(options), delay);
  }

  function afterAdd({ giftIncluded }) {
    cachedCart = null;
    if (!giftIncluded) scheduleEnsure(120, { refreshPopup: true });
  }

  function bindCartUpdates() {
    const eventName = window.PUB_SUB_EVENTS?.cartUpdate;
    if (!eventName || typeof window.subscribe !== 'function') return;
    window.subscribe(eventName, (event) => {
      if (!event || event.source === 'tm-gift-atomic-p0' || event.source === 'product-form') return;
      scheduleEnsure(120, { refreshPopup: false });
    });
  }

  document.addEventListener('pointerover', (event) => {
    if (event.target.closest?.('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]')) {
      window.__tmWarmCheckout?.();
    }
  }, { passive: true, capture: true });

  document.addEventListener('DOMContentLoaded', () => {
    bindCartUpdates();
    if (window.location.pathname.replace(/\/+$/, '') === cartUrl.replace(/\/+$/, '')) {
      scheduleEnsure(80, { refreshPopup: false });
    }
  }, { once: true });

  window.TuMandoGiftAutoAdd = {
    prime,
    buildAddRequest,
    afterAdd,
    ensure: (options = {}) => ensure({ refreshPopup: options.refreshPopup !== false }),
  };
})();
